package com.wintermar.inspection.core.ui.widget

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.wintermar.inspection.core.ui.component.image.Document
import com.wintermar.inspection.core.ui.component.image.util.FileGroupTypes
import com.wintermar.inspection.core.ui.component.image.util.FileModel
import com.wintermar.inspection.core.ui.component.image.util.FileUtil
import com.wintermar.inspection.core.ui.component.image.util.deleteTemporaryFile
import com.wintermar.inspection.core.ui.theme.AppDimension
import com.wintermar.inspection.core.ui.theme.LocalAppTheme
import com.wintermar.inspection.core.ui.theme.color.model.ThemeColor
import inspection.composeapp.generated.resources.Res
import inspection.composeapp.generated.resources.multiple_files_picker
import inspection.composeapp.generated.resources.no_file_added
import inspection.composeapp.generated.resources.no_photo_added
import inspection.composeapp.generated.resources.supported_document_label
import inspection.composeapp.generated.resources.supported_image_label
import inspection.composeapp.generated.resources.total_file
import inspection.composeapp.generated.resources.total_photo
import io.github.vinceglb.filekit.PlatformFile
import io.github.vinceglb.filekit.dialogs.FileKitDialogSettings
import io.github.vinceglb.filekit.dialogs.FileKitMode
import io.github.vinceglb.filekit.dialogs.FileKitType
import io.github.vinceglb.filekit.dialogs.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.extension
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.stringResource

@Composable
fun ImagePicker(
    files: List<FileModel> = emptyList(),
    enabled: Boolean = true,
    maxItems: Int = 5,
    isError: Boolean = false,
    appDimension: AppDimension = LocalAppTheme.current.dimension,
    onResult: (List<FileModel>) -> Unit = {},
    dialogSettings: FileKitDialogSettings = FileKitDialogSettings.createDefault(),
    onLoading: (Boolean) -> Unit
) {
    val supportedExtensions = FileGroupTypes.IMAGES_AND_DOCUMENTS.getExtensionsWithoutDot()
    val scope = rememberCoroutineScope()
    var isLoading by remember {
        mutableStateOf(false)
    }
    val imageFiles by remember(files) {
        derivedStateOf {
            files.filter {
                FileGroupTypes.IMAGES.getExtensionsWithoutDot()
                    .contains(FileUtil.getExtensionFromFileName(it.name))
            }
        }
    }
    val documentFiles by remember(files) {
        derivedStateOf {
            files.filter {
                FileGroupTypes.DOCUMENTS.getExtensionsWithoutDot()
                    .contains(FileUtil.getExtensionFromFileName(it.name))
            }
        }
    }

    LaunchedEffect(isLoading) {
        onLoading(isLoading)
    }

    fun handleOnPickFile(fileList: List<PlatformFile>?) {
        scope.launch {
            withContext(Dispatchers.Default) {
                isLoading = true
                val processedFiles = mutableListOf<PlatformFile>()
                try {
                    val remaining = maxItems - files.size
                    val result: List<FileModel> = if (fileList != null && remaining > 0) {
                        val list = mutableListOf<FileModel>()
                        for (f in fileList.take(remaining)) {
                            list += FileUtil.compressToTargetSize(f)
                            processedFiles.add(f)  // Track files that were actually processed
                        }
                        list
                    } else emptyList()

                    val items: List<FileModel> = files + result
                    onResult(items)
                } finally {
                    isLoading = false
                    // ✅ Delete only the files that were actually processed
                    processedFiles.forEach { platformFile ->
                        deleteTemporaryFile(platformFile)
                    }
                }
            }
        }
    }

    val filePicker = rememberFilePickerLauncher(
        type = FileKitType.File(extensions = supportedExtensions),
        mode = FileKitMode.Multiple(maxItems = maxItems - files.size),
        title = stringResource(Res.string.multiple_files_picker),
        onResult = { file ->
            val filteredFiles = file?.filter { platformFile ->
                val fileExtension = platformFile.extension
                fileExtension in supportedExtensions
            }
            handleOnPickFile(filteredFiles)
        },
        dialogSettings = dialogSettings,
    )

    Box(modifier = Modifier.fillMaxSize()) {
        Column(
            verticalArrangement = Arrangement.spacedBy(
                space = appDimension.spacing.space4,
                alignment = Alignment.Top
            ),
            modifier = Modifier.fillMaxSize()
        ) {
            Row(
                horizontalArrangement = Arrangement.spacedBy(
                    space = appDimension.spacing.space2,
                    alignment = Alignment.CenterHorizontally
                )
            ) {
                AttachContainer(
                    enabled = enabled && files.size < maxItems && !isLoading,
                    isError = isError,
                    onPick = {
                        filePicker.launch()
                    },
                    onTakePhoto = { file ->
                        handleOnPickFile(file?.let { listOf(it) })
                    },
                    onFileDrop = {
                        handleOnPickFile(it)
                    }
                )
            }

            InfoContainer(
                show = imageFiles.isNotEmpty(),
                label = stringResource(Res.string.total_photo, imageFiles.size),
                supportLabel = stringResource(Res.string.supported_image_label),
                emptyLabel = stringResource(Res.string.no_photo_added),
                maxItems = maxItems,
                dimension = appDimension,
                color = LocalAppTheme.current.color
            )

            ImageGrid(
                files = imageFiles,
                showDeleteButton = true,
                width = appDimension.size.size13,
                height = appDimension.size.size13,
                onDelete = {
                    val items = files - it
                    onResult(items)
                })

            InfoContainer(
                show = documentFiles.isNotEmpty(),
                label = stringResource(Res.string.total_file, documentFiles.size),
                supportLabel = stringResource(Res.string.supported_document_label),
                emptyLabel = stringResource(Res.string.no_file_added),
                maxItems = maxItems,
                dimension = appDimension,
                color = LocalAppTheme.current.color
            )

            documentFiles.forEach {
                Document(
                    file = it,
                    showDeleteButton = true,
                    onDelete = {
                        val items = files - it
                        onResult(items)
                    }
                )
            }
            Spacer(modifier = Modifier.height(appDimension.spacing.space4))
        }

        AnimatedVisibility(
            visible = isLoading,
            enter = fadeIn(),
            exit = fadeOut(),
            modifier = Modifier.align(
                Alignment.Center
            )
        ) {
            CircularProgressIndicator()
        }
    }
}

@Composable
expect fun AttachContainer(
    enabled: Boolean,
    isError: Boolean,
    onPick: () -> Unit,
    onTakePhoto: (PlatformFile?) -> Unit = {},
    onFileDrop: (List<PlatformFile>?) -> Unit = { _ -> }
)

@Composable
expect fun InfoContainer(
    show: Boolean,
    label: String,
    supportLabel: String,
    emptyLabel: String,
    maxItems: Int,
    dimension: AppDimension,
    color: ThemeColor
)
